import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeeService } from '../../../services/employeeService';
import { extractList, extractMeta } from '../../../services/api';
import { parsePeriod, CURRENT_PERIOD } from '../../../constants';
import Loading from '../../../shared/Loading';
import EmptyState from '../../../shared/EmptyState';
import Badge from '../../../shared/Badge';
import InputModal from '../components/InputModal';

const TABS = [
  { key: 'active', label: 'Laporan Aktif' },
  { key: 'history', label: 'Riwayat' },
];

const ReportsPage = () => {
  const navigate = useNavigate();

  // Active tab state
  const [activeTab, setActiveTab] = useState('active');

  // Reports (paginated)
  const [reports, setReports] = useState([]);
  const [reportsMeta, setReportsMeta] = useState({ page: 1, last_page: 1, total: 0 });
  const [reportsPage, setReportsPage] = useState(1);

  // History (paginated)
  const [history, setHistory] = useState([]);
  const [historyMeta, setHistoryMeta] = useState({ page: 1, last_page: 1, total: 0 });
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Current report + uniforms
  const [currentReport, setCurrentReport] = useState(null);
  const [uniforms, setUniforms] = useState([]);
  const [currentDetails, setCurrentDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTotalQty, setCurrentTotalQty] = useState(null);

  const [toast, setToast] = useState('');
  const [showInputModal, setShowInputModal] = useState(false);
  const [creating, setCreating] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchActiveData = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, cRes, uRes] = await Promise.all([
        employeeService.getReports({ page: reportsPage }),
        employeeService.getCurrentReport(),
        employeeService.getUniforms(),
      ]);
      const currentRep = cRes.data;
      setReports(extractList(rRes.data));
      setReportsMeta(extractMeta(rRes.data));
      setCurrentReport(currentRep);
      setUniforms(Array.isArray(uRes.data) ? uRes.data : []);
      // Fetch details untuk hitung total qty yang akurat dari current report
      if (currentRep?.uniform_report_id) {
        try {
          const dRes = await employeeService.getReportDetails(currentRep.uniform_report_id, { limit: 100 });
          const items = extractList(dRes.data);
          setCurrentDetails(items);
          setCurrentTotalQty(items.reduce((sum, d) => sum + (Number(d.qty) || 0), 0));
        } catch (_) {
          setCurrentTotalQty(null);
        }
      }
    } catch (err) {
      showToast(err?.message || 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  }, [reportsPage]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await employeeService.getReportsHistory({ page: historyPage });
      setHistory(extractList(res.data));
      setHistoryMeta(extractMeta(res.data));
    } catch (err) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage]);

  useEffect(() => { fetchActiveData(); }, [fetchActiveData]);

  useEffect(() => {
    if (activeTab === 'history') fetchHistory();
  }, [activeTab, fetchHistory]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await employeeService.createReport();
      showToast('Laporan bulan ini berhasil dibuat');
      fetchActiveData();
    } catch (err) {
      showToast(err?.message || 'Gagal membuat laporan');
    } finally {
      setCreating(false);
    }
  };

  const handleInputSave = async ({ uniform_id, qty, url_photo, notes }) => {
    const sudahAda = currentDetails.some((d) => d.uniform_id === uniform_id);
    if (sudahAda) {
      const namaUniform = uniforms.find((u) => u.uniform_id === uniform_id)?.name || 'Seragam ini';
      throw new Error(`${namaUniform} sudah pernah diinput`);
    }
    try {
      await employeeService.addReportDetail(currentReport.uniform_report_id, { uniform_id, qty, url_photo, notes });
      showToast('Data berhasil ditambahkan');
      setShowInputModal(false);
      fetchActiveData();
    } catch (err) {
      throw err;
    }
  };

  const renderReportCard = (report) => {
    const { label } = parsePeriod(report.period);
    const isCurrent = report.period === CURRENT_PERIOD;
    return (
      <div
        key={report.uniform_report_id}
        className={`month-card ${isCurrent ? 'month-card--current' : ''}`}
        onClick={() => navigate(`/employee/reports/${report.uniform_report_id}`)}
      >
        <div className="month-card__left">
          <div className="month-card__name">{label}</div>
          {isCurrent && <span className="month-card__badge">Bulan Aktif</span>}
          <Badge status={report.report_status} />
        </div>
        <div className="month-card__right">
          <div className="month-card__qty">{isCurrent && currentTotalQty !== null ? currentTotalQty : report.total_uniform_received}</div>
          <div className="month-card__unit">pcs</div>
        </div>
        <span className="month-card__arrow">›</span>
      </div>
    );
  };

  const renderPagination = (meta, setPage) => {
    if (meta.last_page <= 1) return null;
    return (
      <div className="pagination">
        <button
          className="btn btn-sm btn-outline"
          disabled={meta.page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          ‹ Sebelumnya
        </button>
        <span className="pagination__info">Hal {meta.page} / {meta.last_page}</span>
        <button
          className="btn btn-sm btn-outline"
          disabled={meta.page >= meta.last_page}
          onClick={() => setPage((p) => p + 1)}
        >
          Berikutnya ›
        </button>
      </div>
    );
  };

  if (loading) return <Loading />;

  const canInput = currentReport && currentReport.report_status === 'draft';

  return (
    <>
      <div className="action-bar">
        <button
          className="btn btn-primary"
          onClick={handleCreate}
          disabled={!!currentReport || creating}
        >
          {creating ? 'Membuat...' : currentReport ? '✓ Bulan Ini Dibuat' : '+ Buat Bulan Ini'}
        </button>
        <div className="input-btn-wrap">
          <button
            className="btn btn-secondary"
            onClick={() => setShowInputModal(true)}
            disabled={!canInput}
          >
            + Input Data
          </button>
          {!currentReport && (
            <span className="input-btn-hint">Buat bulan dulu</span>
          )}
          {currentReport && !canInput && (
            <span className="input-btn-hint">Laporan sudah disubmit</span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`tab-btn ${activeTab === tab.key ? 'tab-btn--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Active Tab */}
      {activeTab === 'active' && (
        <>
          {reports.length === 0 ? (
            <EmptyState icon="📋" title="Belum ada laporan" desc="Buat bulan ini dulu untuk mulai mencatat seragam." />
          ) : (
            <div className="monthly-list">
              {reports.map(renderReportCard)}
            </div>
          )}
          {renderPagination(reportsMeta, setReportsPage)}
        </>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <>
          {historyLoading ? (
            <Loading />
          ) : history.length === 0 ? (
            <EmptyState icon="📂" title="Belum ada riwayat" desc="Laporan yang sudah disubmit atau disetujui akan muncul di sini." />
          ) : (
            <div className="monthly-list">
              {history.map(renderReportCard)}
            </div>
          )}
          {!historyLoading && renderPagination(historyMeta, setHistoryPage)}
        </>
      )}

      {toast && <div className="toast">{toast}</div>}

      {showInputModal && currentReport && (
        <InputModal
          uniforms={uniforms}
          onSave={handleInputSave}
          onClose={() => setShowInputModal(false)}
        />
      )}
    </>
  );
};

export default ReportsPage;
